import { useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'

import type { StartupPhase } from '@/components/startup-screen/state'

type StartupScreenProps = {
  phase: StartupPhase
  errorMessage?: string
  onRetry: () => void
}

const PHASE_MESSAGE: Record<StartupPhase, string> = {
  authenticating: '正在验证身份',
  loading_configuration: '正在加载采购配置',
  loading_resources: '正在加载工作台资源',
  preparing_workspace: '正在准备采购工作台',
  ready: '准备完成',
  error: '暂时无法进入，请稍后重试',
}

const MINIMUM_VISIBLE_MS = 900
const EXIT_FALLBACK_MS = 420

function startupStartedAt() {
  const value = Number(document.documentElement.dataset.xiaocaiStartupStartedAt)
  return Number.isFinite(value) && value > 0 ? value : Date.now()
}

export function StartupScreen({ phase, errorMessage = '', onRetry }: StartupScreenProps) {
  const screen = document.getElementById('xiaocai-startup-screen')
  const portalRoot = document.getElementById('xiaocai-startup-react-root')
  const entryBlocked = document.documentElement.dataset.xiaocaiEntryBlocked === 'true'

  useLayoutEffect(() => {
    if (!screen || entryBlocked) {
      return
    }
    screen.classList.add('is-react-owned')
  }, [entryBlocked, screen])

  useEffect(() => {
    if (!screen || entryBlocked) {
      return
    }
    screen.classList.toggle('is-error', phase === 'error')
    if (phase !== 'ready') {
      document.documentElement.dataset.xiaocaiStartupState = phase
      return
    }

    const finishExit = () => {
      screen.hidden = true
      document.documentElement.dataset.xiaocaiStartupState = 'dismissed'
    }
    const beginExit = () => {
      document.documentElement.dataset.xiaocaiStartupState = 'ready'
      screen.classList.add('is-exiting')
    }
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === screen) {
        finishExit()
      }
    }
    screen.addEventListener('transitionend', handleTransitionEnd)
    const remainingVisibleMs = Math.max(0, MINIMUM_VISIBLE_MS - (Date.now() - startupStartedAt()))
    const exitTimer = window.setTimeout(beginExit, remainingVisibleMs)
    const fallbackTimer = window.setTimeout(finishExit, remainingVisibleMs + EXIT_FALLBACK_MS)
    return () => {
      window.clearTimeout(exitTimer)
      window.clearTimeout(fallbackTimer)
      screen.removeEventListener('transitionend', handleTransitionEnd)
    }
  }, [entryBlocked, phase, screen])

  if (!screen || !portalRoot || entryBlocked) {
    return null
  }

  return createPortal(
    <section
      aria-live="polite"
      className="xiaocai-startup-screen__surface"
      role={phase === 'error' ? 'alert' : 'status'}
    >
      <img className="xiaocai-startup-screen__logo" src="/assets/logo-xiaocai.svg" alt="小采" />
      <p className="xiaocai-startup-screen__welcome">XIAOCAI</p>
      <p className="xiaocai-startup-screen__status">
        <span className="xiaocai-startup-screen__message" key={phase}>
          {phase === 'error' ? errorMessage || PHASE_MESSAGE[phase] : PHASE_MESSAGE[phase]}
        </span>
        {phase !== 'error' ? (
          <span className="xiaocai-startup-screen__dots" aria-hidden="true">
            <span /><span /><span />
          </span>
        ) : null}
      </p>
      {phase === 'error' ? (
        <button className="xiaocai-startup-screen__retry" onClick={onRetry} type="button">
          重试
        </button>
      ) : null}
    </section>,
    portalRoot,
  )
}
